import { Conversation, Message } from '@nextforce/api'
import { SearchBarBaseProps } from '@rneui/base/dist/SearchBar/types'
import { SearchBar as _SearchBar, makeStyles } from '@rneui/themed'
import match from 'autosuggest-highlight/match'
import { isRepository } from 'chat/conversations/utils'
import { escapeRegExp } from 'lodash'
import React, { useEffect, useMemo } from 'react'

const SearchBar = _SearchBar as unknown as React.FC<SearchBarBaseProps>

interface Props {
  conversations: Conversation[]
  messages: Message[]
  searchTerm: string
  setSearchTerm: (searchTerm: string) => void
  onChangeConversationName: (filteredConversations: Conversation[]) => void
  onChangeMessage: (filteredMessages: Conversation[]) => void
  onChangeSender: (filteredSender: Conversation[]) => void
}

export function MessagesSearchBar({
  conversations,
  messages,
  searchTerm,
  setSearchTerm,
  onChangeConversationName,
  onChangeMessage,
  onChangeSender,
}: Props) {
  const styles = useStyles()
  const repositoryConversationIds = useMemo(
    () => conversations.filter(c => isRepository(c)).map(c => c.id),
    [conversations]
  )
  const nonRepositoryMessages = useMemo(
    () =>
      messages.filter(
        m => !repositoryConversationIds.includes(m.conversationId)
      ),
    [messages, repositoryConversationIds]
  )

  useEffect(() => {
    const uppercaseSearchText = searchTerm.toUpperCase()
    const regex = new RegExp(escapeRegExp(searchTerm.trim()), 'i')

    const filteredMessages = nonRepositoryMessages.flatMap(message => {
      if (message.body && regex.test(message.body)) {
        return {
          ...message,
          matches: match(message.body, searchTerm, {
            insideWords: true,
            findAllOccurrences: true,
          }),
        }
      }
      return []
    })

    const filteredMessagesConversations = filteredMessages
      .map(message => ({
        ...conversations?.find(
          conversation => conversation.id === message.conversationId
        )!,
        status: { recentMessage: message },
      }))
      .sort(
        (a, b) =>
          b.status.recentMessage.createdAt - a.status.recentMessage.createdAt
      )

    const filteredSenders = nonRepositoryMessages
      .filter(
        message =>
          message.user.firstName
            ?.toUpperCase()
            .startsWith(uppercaseSearchText) ||
          message.user.lastName?.toUpperCase().startsWith(uppercaseSearchText)
      )
      .map(message => ({
        ...conversations?.find(
          conversation => conversation.id === message.conversationId
        )!,
        status: { recentMessage: message },
      }))
      .filter(c => c.type !== 'dm')
      .sort(
        (a, b) =>
          b.status.recentMessage.createdAt - a.status.recentMessage.createdAt
      )

    const filteredConversationNames = conversations.filter(conversation => {
      const name = conversation.name
      return name.toUpperCase().indexOf(uppercaseSearchText) === 0
    })

    onChangeConversationName(filteredConversationNames)
    onChangeMessage(filteredMessagesConversations)
    onChangeSender(filteredSenders)
  }, [
    conversations,
    nonRepositoryMessages,
    onChangeConversationName,
    onChangeMessage,
    onChangeSender,
    searchTerm,
  ])

  return (
    <SearchBar
      placeholder="Search by Name, Keyword, Tag"
      onChangeText={setSearchTerm}
      value={searchTerm}
      containerStyle={styles.container}
      inputContainerStyle={styles.inputContainer}
    />
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors?.white,
    padding: 0,
    marginBottom: 10,
    borderBottomWidth: 0,
    borderTopWidth: 0,
  },
  inputContainer: {
    backgroundColor: theme.colors?.white,
  },
}))
